import { Box, IconButton, Slider, Typography } from "@mui/material";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    BarChart,
    Bar,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import moment from "moment-timezone";


import NavigationRefresh from "@mui/icons-material/Refresh";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { apiRoutes, fetchJson } from "@/src/services";
import { buildQueryUrl } from "@/src/utils";

interface Props {
    timezone: string,
    // months?: number
}

const colors: string[] = ['#B80000', '#DB3E00', '#FCCB00', '#008B02', '#006B76', '#1273DE', '#004DCF', '#5300EB', '#EB9694', '#FAD0C3', '#FEF3BD', '#C1E1C5', '#BEDADC', '#C4DEF6', '#BED3F3', '#D4C4FB'];
const currencies = ["USD", "EUR", "GBP", "USDT", "BUSD"] as const
type Currencies = typeof currencies[number]
type CurrencyValues = Record<Currencies, number>

interface FetchedItem {
    amount: number,
    count: number,
    _id: {
        currency: Currencies,
        date: string
    }
}

interface DatedRecord {
    date: Date,
    amount: CurrencyValues,//{USD:135,EUR:130}
    count: CurrencyValues
}

interface StatRecord {
    amount: number,
    count: number
}

type RecordSource = { _id: Date } & Record<string, StatRecord>;

//{USD:0,EUR:0,GBP:0}
const initCurrencyValues = () => currencies.reduce((acc, currency) => ({ ...acc, [currency]: 0 }), {} as CurrencyValues)

export function DepositsByMonth({ timezone }: Props) {
    const [months, setMonths] = useState(12)
    const startDate = moment().subtract(months - 1, "month").tz(timezone).startOf("month");
    const templateMonthArray: DatedRecord[] = Array.from({ length: months }, (_, i) => {
        const result: DatedRecord = {
            date: startDate.toDate(),
            amount: initCurrencyValues(),
            count: initCurrencyValues()
        };
        startDate.add(1, 'month');
        return result;
    });

    const { data, refetch } = useQuery<DatedRecord[]>({
        queryKey: ['deposits-by-month', timezone, months],
        queryFn: () => fetchJson(buildQueryUrl(apiRoutes.dashboard.depositsByMonth2, { months, timezone }))
            .then(resp => resp.json),

        staleTime: Infinity,
        refetchOnWindowFocus: false
    }
    );


    return (
        <Box>
            <IconButton onClick={() => refetch()} aria-label="refresh" color="primary"><NavigationRefresh /></IconButton>
            <Typography variant="h6" align="center">Deposits By Month</Typography>
            <ResponsiveContainer width="100%" height={220}>

                <BarChart
                    data={data}
                    margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
                    barCategoryGap={"1%"}
                    barGap={1}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="_id" tick={{ fontSize: 12 }}
                        tickFormatter={(value) => (!value || value === "auto") ? value : moment(value).tz(timezone).format('MMM Y')} />
                    <YAxis yAxisId="left" orientation="left" stroke="#B0B5B3" tick={{ fontSize: 12 }} />
                    <Tooltip labelFormatter={(value: any) => moment(value).tz(timezone).format('MMM Y')} />
                    <Legend formatter={(value: string) => value.split('.').at(0)} />
                    {currencies.map((currency, index) =>
                        <Bar yAxisId="left" type="monotone" dataKey={`${currency}.amount`}
                            key={currency} fill={colors[index]} />)}
                </BarChart>
            </ResponsiveContainer>
            <Box px={"15%"}>
                <Slider
                    value={months}
                    onChange={(e, newValue) => setMonths(newValue as number)}
                    marks={[{ value: 12, label: "12" }, { value: 24, label: "24" }, { value: 36, label: "36" }]}
                    // valueLabelDisplay="on"
                    step={null}
                    min={10} max={40}
                />
            </Box>
        </Box>
    )
};
